import { useState, useEffect } from 'react';
import { onSnapshot, query, collection, orderBy, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db, uploadPDF, deleteFile } from '../config/firebase';

export const useCommuniques = () => {
  const [communiques, setCommuniques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    const q = query(collection(db, 'communiques'), orderBy('date', 'desc'));
    
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          date: doc.data().date.toDate ? doc.data().date.toDate() : new Date(doc.data().date)
        }));
        console.log('Communiqués chargés:', data.length, data);
        setCommuniques(data);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Erreur Firestore:', err);
        if (err.code === 'permission-denied') {
          setError('Permission refusée. Vérifiez les règles Firestore.');
        } else {
          setError(err.message);
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const createCommunique = async (data, pdfFile) => {
    try {
      let pdfData = null;
      
      // Upload du PDF si fourni
      if (pdfFile) {
        setUploadProgress(10);
        pdfData = await uploadPDF(pdfFile, 'communiques', (progress) => {
          setUploadProgress(Math.round(progress));
        });
      }

      await addDoc(collection(db, 'communiques'), {
        ...data,
        pdfUrl: pdfData.url || null,
        pdfPath: pdfData.path || null,
        fileSize: pdfData.size || pdfFile.size || 0,
        date: new Date(),
        views: 0
      });
      
      setUploadProgress(0);
      return { success: true };
    } catch (err) {
      console.error('Erreur création communiqué:', err);
      setUploadProgress(0);
      return { success: false, error: err.message };
    }
  };

  const editCommunique = async (id, data, pdfFile, deleteOldPdf = false) => {
    try {
      const communiqueDoc = doc(db, 'communiques', id);
      let updateData = { ...data };

      // Si on doit supprimer l'ancien PDF ou uploader un nouveau
      if (pdfFile) {
        // Supprimer l'ancien PDF si existe
        if (deleteOldPdf && data.oldPdfPath) {
          try {
            await deleteFile(data.oldPdfPath);
          } catch (e) {
            console.warn('Ancien PDF non trouvé:', e);
          }
        }

        // Upload nouveau PDF
        setUploadProgress(10);
        const pdfData = await uploadPDF(pdfFile, 'communiques');
        setUploadProgress(100);
        
        updateData.pdfUrl = pdfData.url;
        updateData.pdfPath = pdfData.path;
        updateData.fileSize = pdfData.size;
      }

      delete updateData.oldPdfPath;
      await updateDoc(communiqueDoc, updateData);
      
      setUploadProgress(0);
      return { success: true };
    } catch (err) {
      console.error('Erreur modification communiqué:', err);
      setUploadProgress(0);
      return { success: false, error: err.message };
    }
  };

  const removeCommunique = async (id, pdfPath) => {
    try {
      // Supprimer le PDF du Storage si existe
      if (pdfPath) {
        try {
          await deleteFile(pdfPath);
        } catch (e) {
          console.warn('Fichier non trouvé dans Storage:', e);
        }
      }

      const communiqueDoc = doc(db, 'communiques', id);
      await deleteDoc(communiqueDoc);
      return { success: true };
    } catch (err) {
      console.error('Erreur suppression communiqué:', err);
      return { success: false, error: err.message };
    }
  };

  return {
    communiques,
    loading,
    error,
    uploadProgress,
    createCommunique,
    editCommunique,
    removeCommunique
  };
};
